// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useRef, useState } from 'react';
import clsx from 'clsx';

import {
  GeneratedAnalyticsMetadataFragment,
  getAnalyticsMetadataAttribute,
} from '@amzn/awsui-component-toolkit/internal/analytics-metadata';

import { getBaseProps } from '../internal/base-component';
import AbstractSwitch from '../internal/components/abstract-switch';
import { useFormFieldContext } from '../internal/context/form-field-context';
import { fireNonCancelableEvent } from '../internal/events';
import useForwardFocus from '../internal/hooks/forward-focus';
import { InternalBaseComponentProps } from '../internal/hooks/use-base-component';
import WithNativeAttributes from '../internal/utils/with-native-attributes';
import { GeneratedAnalyticsMetadataToggleComponent } from './analytics-metadata/interfaces';
import { ToggleProps } from './interfaces';
import { getAbstractSwitchStyles, getStyledControlStyle } from './style';

import styles from './styles.css.js';

interface InternalToggleProps extends ToggleProps, InternalBaseComponentProps {
  styleClassNames?: StyleClassNames;
  __injectAnalyticsComponentMetadata?: boolean;
}

// Style API v2
interface StyleClassNames {
  control?: string;
}

const InternalToggle = React.forwardRef<ToggleProps.Ref, InternalToggleProps>(
  (
    {
      controlId,
      checked,
      name,
      disabled,
      readOnly,
      children,
      description,
      ariaLabel,
      ariaControls,
      onFocus,
      onBlur,
      onChange,
      nativeInputAttributes,
      __internalRootRef,
      style,
      styleClassNames,
      __injectAnalyticsComponentMetadata,
      ...rest
    },
    ref
  ) => {
    const { ariaDescribedby, ariaLabelledby } = useFormFieldContext(rest);
    const baseProps = getBaseProps(rest);
    const checkboxRef = useRef<HTMLInputElement>(null);

    // The handle's follow-through animation only plays once the state has changed since mount.
    const [initialChecked] = useState(!!checked);
    const [hasChanged, setHasChanged] = useState(false);
    if (!hasChanged && !!checked !== initialChecked) {
      setHasChanged(true);
    }

    const analyticsMetadata: GeneratedAnalyticsMetadataFragment = {};
    const analyticsComponentMetadata: GeneratedAnalyticsMetadataToggleComponent = {
      name: 'awsui.Toggle',
      label: { root: 'self' },
      properties: {
        checked: `${!!checked}`,
      },
    };

    if (__injectAnalyticsComponentMetadata) {
      analyticsMetadata.component = analyticsComponentMetadata;
    }

    if (!disabled && !readOnly) {
      analyticsMetadata.action = !checked ? 'select' : 'deselect';
    }
    useForwardFocus(ref, checkboxRef);

    return (
      <AbstractSwitch
        {...baseProps}
        className={clsx(styles.root, baseProps.className)}
        controlClassName={clsx(styleClassNames?.control, styles['toggle-control'], {
          [styles['toggle-control-checked']]: checked,
          [styles['toggle-control-disabled']]: disabled,
          [styles['toggle-control-readonly']]: readOnly,
        })}
        outlineClassName={styles.outline}
        controlId={controlId}
        disabled={disabled}
        readOnly={readOnly}
        label={children}
        description={description}
        descriptionBottomPadding={true}
        ariaLabel={ariaLabel}
        ariaLabelledby={ariaLabelledby}
        ariaDescribedby={ariaDescribedby}
        ariaControls={ariaControls}
        nativeControl={nativeControlProps => (
          <WithNativeAttributes
            {...nativeControlProps}
            tag="input"
            componentName="Toggle"
            nativeAttributes={nativeInputAttributes}
            ref={checkboxRef}
            type="checkbox"
            checked={checked}
            name={name}
            aria-disabled={readOnly && !disabled ? 'true' : undefined}
            onFocus={() => fireNonCancelableEvent(onFocus)}
            onBlur={() => fireNonCancelableEvent(onBlur)}
            // empty handler to suppress React controllability warning
            onChange={() => {}}
          />
        )}
        onClick={() => {
          checkboxRef.current?.focus();
          fireNonCancelableEvent(onChange, { checked: !checked });
        }}
        styledControl={
          /*Using span, not div for HTML validity*/
          <span
            className={clsx(styles['toggle-handle'], {
              [styles['toggle-handle-checked']]: checked,
              [styles['toggle-handle-disabled']]: disabled,
              [styles['toggle-handle-readonly']]: readOnly,
            })}
            data-awsui-motion-ready={hasChanged ? '' : undefined}
            style={getStyledControlStyle(style, checked, disabled, readOnly)}
          />
        }
        style={getAbstractSwitchStyles(style, checked, disabled, readOnly)}
        __internalRootRef={__internalRootRef}
        {...getAnalyticsMetadataAttribute(analyticsMetadata)}
      />
    );
  }
);

export default InternalToggle;



// WEBPACK FOOTER //
// /Volumes/workplace/src/toggle/internal.tsx